import { formatDate } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import * as _ from 'lodash';
import { Subscription } from 'rxjs';
import { avoidEmptyValue } from 'src/app/admin/location/holiday/shared/validate';
import { TextConfigurationService } from 'src/app/admin/text-configuration/shared/services/text-configuration.service';
import { getServices, getSpanishSpeakerPreferred, getStaffMembers } from 'src/app/lobby/lobby-check-in/state';
import * as LobbyCheckInPageActions from 'src/app/lobby/lobby-check-in/state/actions/lobby-check-in-page.actions';
import { EditCheckInModel } from 'src/app/lobby/shared/models/lobby-edit.model';
import { StaffMember } from 'src/app/lobby/shared/models/staff-member';
import { LobbyEntryService } from 'src/app/lobby/shared/services/lobby-entry.service';
import { getCurrentTenant, getSystemConfigurations } from 'src/app/state';
import { State } from 'src/app/state/app.state';
import { ConfigurationGroup } from '../../enums/configurationGroup.enum';
import { MessageTypeName } from '../../enums/messageType.enum';
import { MaskingType } from '../../enums/mskingType';
import { SystemConfigurationName } from '../../enums/systemConfigurationName.enum';
import { FixedNumberOfCharsMaskedFromEnd, Masker } from '../../masking/partial-masking';
import { ToastService } from '../../services/toast.service';
import { readCastedValue } from '../../utils/read-casted-value.function';
import EmailValidator from '../../validators/generic-email-validator';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-lobby-edit',
  templateUrl: './lobby-edit.component.html',
  styleUrls: ['./lobby-edit.component.scss']
}) 
export class LobbyEditComponent implements OnInit, OnDestroy {
  @Input() public dialogOpened: boolean = false;
  @Input() public dataItem: any;
  public lobbyData: any;
  public edittedLobbyEntryLocationId: number = 0;
  public staffMembers: StaffMember[] = [];
  public lobbyEditForm: FormGroup = new FormGroup({});
  public currentDate = new Date();
  public allServices: any;
  public tenantId: number = 0;
  public dateSelectionMaxLimit = formatDate(this.currentDate.setDate(this.currentDate.getDate() - 3), 'yyyy-MM-dd', 'en');
  public dropdownSettings = {};
  public lobbyConfiguration: any;
  private accountNumberValue: string = "";
  private masker?: Masker;
  public enableTextMessages: boolean = false;

  lobbyEditSubscriptions = new Subscription();
  staffMemberRequiredMessage: string = '';
  staffMemberRequired: boolean = false;
  spanishSpeakerPrefferChecked$ = this.store.select(getSpanishSpeakerPreferred);

  constructor(public lobbyEntryService: LobbyEntryService,
    public fb: FormBuilder,
    private store: Store<State>,
    private toastService: ToastService,
    private textConfigurationService: TextConfigurationService,
    private translateService: TranslateService) { }

  ngOnInit() {
    this.dropdownSettings = {
      singleSelection: false,
      idField: 'id',
      textField: 'name',
      selectAllText: this.translateService.instant('_General_Select_All_Text_'),
      unSelectAllText: this.translateService.instant('_General_Unselect_All_Ts_Text_'),
      allowSearchFilter: true,
      searchPlaceholderText: this.translateService.instant('_General_Search_Text_')
    };
    this.masker = new Masker();
    this.masker.setStrategy(new FixedNumberOfCharsMaskedFromEnd(4));
    this.createForm();
    this.lobbyEditSubscriptions.add(
      this.store.select(getSystemConfigurations).subscribe(
        (configurations) => {
          const lobbyConfigurations = configurations.filter(c => c.configurationGroup === ConfigurationGroup.LobbySettings);
          if (lobbyConfigurations.length > 0) {
            this.lobbyConfiguration = {
              EnableAccountNumber: readCastedValue(lobbyConfigurations.find(ac => ac.configurationName === SystemConfigurationName.EnableAccountNumber)),
              EnablePhoneNumber: readCastedValue(lobbyConfigurations.find(ac => ac.configurationName === SystemConfigurationName.EnablePhoneNumber)),
              EnableEmailAddress: readCastedValue(lobbyConfigurations.find(ac => ac.configurationName === SystemConfigurationName.EnableEmailAddress)),
              EnableDateOfBirth: readCastedValue(lobbyConfigurations.find(ac => ac.configurationName === SystemConfigurationName.EnableDateOfBirth)),
              EnableCustomField: readCastedValue(lobbyConfigurations.find(ac => ac.configurationName === SystemConfigurationName.EnableCustomField)),
              ShowCategoriesInLobby: readCastedValue(lobbyConfigurations.find(ac => ac.configurationName === SystemConfigurationName.ShowCategories)),
              CustomFieldText: readCastedValue(lobbyConfigurations.find(x => x.configurationName == SystemConfigurationName.CustomFieldText)),
              AccountNumberDisplayText: readCastedValue(lobbyConfigurations.find(x => x.configurationName == SystemConfigurationName.AccountNumberDisplayText)),
              AccountNumberMaskingOption: readCastedValue(lobbyConfigurations.find(ac => ac.configurationName === SystemConfigurationName.AccountNumberMaskingOption)),
              MakeStaffMemberSelectionMandatory: readCastedValue(lobbyConfigurations.find(ac => ac.configurationName === SystemConfigurationName.MakeStaffMemberSelectionMandatory)),
              EnableTextMessageNotification: readCastedValue(configurations.find(x => x.configurationName == SystemConfigurationName.EnableTextMessageNotification))
            };
          }
        }
      )
    );
    this.lobbyEditSubscriptions.add(this.store.select(getCurrentTenant).subscribe(t => this.tenantId = t.tenantID));
    this.setTextMessageConfiguration();
  }

  ngOnDestroy(): void {
    this.lobbyEditSubscriptions.unsubscribe();
  }

  private setTextMessageConfiguration() {
    const configs = [MessageTypeName.CheckedInCustomer_WalkIn,
    MessageTypeName.CheckedInCustomer_Appointment,
    MessageTypeName.Customer_NextInQueue];

    for (const configKey of configs) {
      this.lobbyEditSubscriptions.add(
        this.textConfigurationService.fetchConfigurationPublic(configKey).subscribe((config) => {
          if (JSON.parse(config.configData).OptIn) {
            this.enableTextMessages = true;
          }
        })
      );
    }
  }

  /**
   * Save edited lobby record
   * @param event
   */
  public saveEditedlLobbyRecord(event: any) {
    const editChecking = new EditCheckInModel();
    editChecking.LocationId = this.edittedLobbyEntryLocationId;
    editChecking.AccountNumber = this.accountNumberValue == '' ? event.accountNumber : this.accountNumberValue;
    editChecking.DateOfBirth = event.dateOfBirth;
    editChecking.EmailAddress = event.email;
    editChecking.FirstName = event.firstName;
    editChecking.LastName = event.lastName;
    editChecking.Notes = event.notes;
    let userRequestedId = null;
    let groupRequestedId = null;
    if (event.personRequested != -1) {
      this.staffMemberRequiredMessage = '';
      if (this.staffMembers.find(x => x.userId == event.personRequested) != undefined) {
        const isNotificationGroups = this.staffMembers.find(x => x.userId == event.personRequested)?.isNotificationGroup;
        if (isNotificationGroups) {
          groupRequestedId = Number(event.personRequested);
        }
        else {
          userRequestedId = Number(event.personRequested);
        }
      }else if (this.lobbyConfiguration.MakeStaffMemberSelectionMandatory) {
        this.staffMemberRequiredMessage = this.translateService.instant('_General_Staff_Memeber_Required_Ts_Text_');
        this.staffMemberRequired= true;
        return;
      }
    }
    editChecking.GroupRequested = groupRequestedId || undefined;
    editChecking.UserRequested = userRequestedId || undefined;

    editChecking.PhoneNumber = event.phoneNumber;
    editChecking.RecieveTextMessage = event.recieveTextUpdate;
    editChecking.ServiceRequested = event.requests.map((obj: any) => obj.id);
    editChecking.PreferredLanguage = event.spanishSpeaker === true ? 3 : 1;

    if (this.lobbyConfiguration.EnableCustomField) {
      editChecking.CustomFieldValues = { 1: `${event.customFieldValues}` };
    } else {
      editChecking.CustomFieldValues = {};
    }

    this.lobbyEntryService.updateCheckin(this.lobbyData.lobbyEntryId, editChecking).subscribe((data) => {
      this.dialogOpened = false;
      this.allServices = [];
    }, errorObj => {
      console.log('lobby edit component error');
      this.toastService.showError(errorObj.error);
    })
  }

  /**
   * Clear values and close edit popup
   */
  public close() {
    this.dialogOpened = false;
    this.lobbyData = null;
    this.lobbyEditForm.patchValue({
      accountNumber: ""
    });
  }

  /**
   * Fetch editing lobby entry record
   * and relevent information and initate value patching
   */
  public open() {
    this.lobbyEntryService.getLobbyEntryDetails(this.dataItem.lobbyEntryId).subscribe((lobbyData) => {
      this.lobbyData = lobbyData;
      this.edittedLobbyEntryLocationId = this.dataItem.locationId;
      this.store.dispatch(LobbyCheckInPageActions.setLocationId({
        locationId: this.dataItem.locationId
      }));
      this.store.dispatch(LobbyCheckInPageActions.loadLobbyDetails({
        locationId: this.edittedLobbyEntryLocationId
      }));
      this.store.dispatch(LobbyCheckInPageActions.loadStaffMembers({
        tenantId: this.tenantId,
        locationId: this.edittedLobbyEntryLocationId,
        showInLobby: true
      }));
      this.store.dispatch(LobbyCheckInPageActions.loadServices({
        locationId: this.edittedLobbyEntryLocationId,
        showInLobby: true,
        showCategory: false
      }));
      this.getAllStaffMembers();
      this.createForm();
      this.getAllServices();
      this.setValuesToLobbyEditForm();
      this.dialogOpened = true;
    })
  }

  /**
   * Fetch all services from store
   */
  public getAllServices() {
    this.store.select(getServices(false)).subscribe((Services) => {
      const totalServices = Object.values(Services);
      this.allServices = _.map(totalServices, 'service');
    })
  }

  /**
   * Get all staff members from store
   */
  public getAllStaffMembers() {
    this.store.select(getStaffMembers).subscribe((allStaffMembers) => {
      this.staffMembers = allStaffMembers;
    })
  }

  /**
   * Create form with minimum validation in checkin
   */
  public createForm() {
    this.lobbyEditForm = this.fb.group({
      accountNumber: [null, [Validators.pattern(/^[^<>&]*$/)]],
      firstName: [null, [Validators.required, Validators.pattern(/^[^<>&]*$/), avoidEmptyValue]],
      lastName: [null, [Validators.required, Validators.pattern(/^[^<>&]*$/), avoidEmptyValue]],
      dateOfBirth: [null, []],
      phoneNumber: ['', { validators: [Validators.pattern(/^[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4}$/)], updateOn: 'blur' }],
      recieveTextUpdate: false,
      email: [null, [EmailValidator()]],
      personRequested: [null, []],
      spanishSpeaker: [null, []],
      requests: [null, []],
      notes: [null, [Validators.pattern(/^[^<>&]*$/)]],
      customFieldValues: [null, [Validators.pattern(/^[^<>&]*$/)]]
    });
  }

  /**
   * Patch fetched values to the Form
   */
  public setValuesToLobbyEditForm() {
    this.lobbyEditForm.patchValue({
      dateOfBirth: this.lobbyData.dateOfBirth,
      recieveTextUpdate: this.lobbyData.recieveTextMessage,
      phoneNumber: this.lobbyData.phoneNumber,
      spanishSpeaker: this.lobbyData.preferredLanguage.languageId === 1 ? false : true,
      accountNumber: this.lobbyData.accountNumber,
      firstName: this.lobbyData.firstName,
      lastName: this.lobbyData.lastName,
      email: this.lobbyData.emailAddress,
      notes: this.lobbyData.notes,
      customFieldValues: (this.lobbyData.customFieldValues.length > 0) ? this.lobbyData.customFieldValues[0].customFieldValue : ''
    });
    if (this.lobbyData.preferredLanguage != null && this.lobbyData.preferredLanguage.languageId == 3) {
      this.store.dispatch(LobbyCheckInPageActions.spanishSpeakerPreferredChange({ spanishSpeakerPreferred: true }));
    }
    else {
      this.store.dispatch(LobbyCheckInPageActions.spanishSpeakerPreferredChange({ spanishSpeakerPreferred: false }));
    }
    this.accountNumberValue = this.lobbyData.accountNumber;
    if (this.lobbyData.userRequested) {
      this.lobbyEditForm.patchValue({
        personRequested: this.lobbyData.userRequested.userId
      });
    }
    if (this.lobbyData.groupRequested) {

      this.lobbyEditForm.patchValue({
        personRequested: this.lobbyData.groupRequested.userId
      });

    }
    if (this.lobbyData.userRequested == null && this.lobbyData.groupRequested == null) {
      this.lobbyEditForm.patchValue({
        personRequested: -1,
      });
    }

    if (this.lobbyData.requestedServices) {
      this.lobbyEditForm.patchValue({
        requests: this.lobbyData.requestedServices
      });
    }

    this.EnableReceiveTextMessageCheckBox();
    this.lobbyEditForm.updateValueAndValidity();
  }

  EnableReceiveTextMessageCheckBox() {
    if (this.lobbyEditForm?.controls.phoneNumber.invalid || this.lobbyEditForm?.controls.phoneNumber.value === '') {
      this.lobbyEditForm.controls.recieveTextUpdate.disable();
      this.lobbyEditForm.controls.recieveTextUpdate.setValue(false);
    } else {
      this.lobbyEditForm.controls.recieveTextUpdate.enable();
    }
  }

  /**
   * Checks for invalid form controls
   * @returns error controls
   */
  public findInvalidControls() {
    const invalid = [];
    const controls = this.lobbyEditForm.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        invalid.push(name);
      }
    }
    return invalid;
  }

  public keypress(event: KeyboardEvent) {
    // Prevent typing spaces as the first character
    if ((event.target as HTMLInputElement).selectionStart === 0 && event.key === ' ') {
      event.preventDefault();
    }
  }

  onAccountNumberKeyUp(event: KeyboardEvent): void {
    const accountNumber: string = this.lobbyEditForm.get('accountNumber')!.value;
    if (accountNumber.includes('●')) {
      if (event.key.length === 1)
        this.lobbyEditForm.get('accountNumber')!.setValue(event.key);
      else
        this.lobbyEditForm.get('accountNumber')!.setValue('');
    }
  }

  get accountNumber() {
    return this.lobbyEditForm.get('accountNumber');
  }

  accountNumberFocusOut() {
    this.accountNumberValue = this.accountNumber?.value;
    const maskingType = this.lobbyConfiguration.AccountNumberMaskingOption;
    const eMaskingType = maskingType == 'Full Masking' ? MaskingType.FULL : maskingType == 'Partial Masking' ? MaskingType.PARTIAL : MaskingType.NONE;
    this.lobbyEditForm.patchValue({
      accountNumber: this.maskValue(this.accountNumberValue, eMaskingType)
    });
  }

  onAccountNumberChange(accountNumberEvent: Event) {
    const accountNumber = (accountNumberEvent.target as HTMLInputElement).value;
    this.accountNumber?.setValue(accountNumber);
  }

  maskValue(value: string, type: MaskingType) {
    switch (type) {
      case MaskingType.NONE:
        return value;

      case MaskingType.FULL:
        return value.replace(/./g, '*');

      case MaskingType.PARTIAL:
        return this.masker?.mask(value);
    }
  }

  changeRequestedUser(event: Event) {
    if ((event.target as HTMLInputElement).value === '0: -1') {
      if (this.lobbyConfiguration.MakeStaffMemberSelectionMandatory) {
        this.staffMemberRequiredMessage = this.translateService.instant('_General_Staff_Memeber_Required_Ts_Text_');
        this.staffMemberRequired= true;
        return;
      }

    }
    else {
      this.staffMemberRequiredMessage = '';
      this.staffMemberRequired = false;
    }
  }

  public blur() {
    this.EnableReceiveTextMessageCheckBox();
  }

  spanishSpeakerCheckBoxClick(IsCheckedEvent: Event) {
    const IsChecked = (IsCheckedEvent.target as HTMLInputElement).checked
    this.store.dispatch(LobbyCheckInPageActions.spanishSpeakerPreferredChange({ spanishSpeakerPreferred: IsChecked }));
  }
}
